import {
  Avatar,
  BreadcrumbHeading,
  CancelledIcon,
  Chip,
  DetailsPanel,
  EditIcon,
  FormatInformation,
  IconButton,
  Spinner,
  Typography,
  useNotifications,
} from '@weareopenr/component-library';
import styles from './User.module.scss';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IUser } from 'src/types';
import { getUser as getUserApi } from 'src/api/getUser';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { useState } from 'react';
import UserModal from 'src/components/UserModal/UserModal';
import { usePermissions, useUserTracking } from 'src/hooks';
import { useGetBrands } from 'src/hooks/useGetBrands/useGetBrands';
import { IBrandResponse } from 'src/api/getBrands/types';
import { InformationField } from 'src/components/InformationField';
import { useDeactivateUser } from 'src/components/DeactivateUser';
import { IOption } from '@weareopenr/component-library/build/components/Dropdown/Dropdown';

const renderPermissions = (permissions?: string[]) => {
  if (!permissions || permissions.length === 0) {
    return 'Base Permissions Only';
  }

  const permissionMap: Record<string, string[]> = {};
  for (const permission of permissions) {
    const prefix = permission.split(':')[0];
    if (!permissionMap[prefix]) {
      permissionMap[prefix] = [];
    }
    permissionMap[prefix].push(permission);
  }

  return (
    <>
      {Object.keys(permissionMap)
        .toSorted((a, b) => a.localeCompare(b))
        .map((groupKey) => {
          return (
            <div
              className={styles['permissions-container']}
              id="permissions-container"
              key={groupKey}
            >
              {permissionMap[groupKey]
                .toSorted((a, b) => a.localeCompare(b))
                .map((permission) => (
                  <Chip key={permission} label={permission} backgroundColour="blueberry" />
                ))}
            </div>
          );
        })}
    </>
  );
};
const getInitials = (name: string) => {
  if (name.length < 2) return name.toUpperCase();
  return name[0].toUpperCase() + name[1].toLowerCase();
};
const renderBrands = (brands: IBrandResponse[]) => {
  if (brands.length === 0) {
    return <Typography size="medium">No brands assigned</Typography>;
  }

  return (
    <div className={styles['brands-list']}>
      {brands.map((brand) => (
        <div className={styles['brand']} key={brand.brand_id}>
          <Avatar
            {...({
              imageUrl: brand.logo_url || undefined,
              initials: brand.logo_url ? '' : getInitials(brand.name),
              variant: 'custom',
              colourVariant: 'blueberry',
            } as any)}
          />
          <Typography size="medium">{brand.name}</Typography>
        </div>
      ))}
    </div>
  );
};

export const User = () => {
  const { userId } = useParams<{ userId: string }>();
  const createNotification = useNotifications();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const { canEditUsers, canDeactivateUsers } = usePermissions();
  const { data: brands = [] } = useGetBrands();
  const { captureException } = useUserTracking();
  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery<IUser>({
    queryKey: ['users', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      const response = await getUserApi(userId);
      return response.data;
    },
    enabled: !!userId,
    onError: (error) => {
      createNotification({
        severity: 'error',
        message: 'Something went wrong whilst fetching brands',
      });
      captureException(error);
    },
  });
  const { roles, isLoading: isRolesLoading } = useRoles();
  const role = roles?.find((role) => role.slug === user?.role);

  const deactivateUserHook = useDeactivateUser();

  if (isLoading || isRolesLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="large" />
      </div>
    );
  }

  const handleEditClick = canEditUsers ? () => setShowEditModal(true) : undefined;

  const headerActions: IOption[] = [];
  if (canDeactivateUsers && user) {
    headerActions.push({
      id: 'deactivate',
      name: 'Deactivate',
      onOptionClick: () => deactivateUserHook.openPrompt(user),
      icon: CancelledIcon,
      hoverColour: 'tomato',
    });
  }

  const userBrands: IBrandResponse[] =
    user?.brands?.[0] === '*'
      ? brands
      : user?.brands
          ?.map((brand) => brands?.find((b) => b.brand_id === brand))
          .filter((brand): brand is IBrandResponse => brand !== undefined) || [];

  return (
    <>
      {canEditUsers && (
        <UserModal
          show={showEditModal}
          closeModal={() => {
            refetch();
            setShowEditModal(false);
          }}
          user={user}
        />
      )}
      {deactivateUserHook.PromptComponent}
      <div className={styles.container}>
        <BreadcrumbHeading
          breadcrumbs={[
            { label: 'Users', path: '/users' },
            { label: `${user?.first_name} ${user?.last_name}`, path: `/users/${user?.id}` },
          ]}
          optionMenuItems={headerActions.length > 0 ? headerActions : undefined}
        />
        <div className={styles['details-container']}>
          <DetailsPanel title="User Details" className={styles['details-panel']}>
            <FormatInformation heading="First Name">{user?.first_name}</FormatInformation>
            <FormatInformation heading="Last Name">{user?.last_name}</FormatInformation>
            <FormatInformation heading="Email">{user?.email}</FormatInformation>
          </DetailsPanel>
          <DetailsPanel
            title="Access"
            className={styles['details-panel']}
            action={
              canEditUsers ? (
                <IconButton
                  onClick={handleEditClick}
                  icon={EditIcon}
                  aria-label="Edit User Access"
                />
              ) : undefined
            }
          >
            <div className={styles['access-columns']}>
              <div className={styles['access-column']}>
                <FormatInformation heading="Role">{role?.name}</FormatInformation>
                <InformationField
                  heading="Permissions"
                  information={renderPermissions(role?.permissions)}
                />
              </div>
              <div className={styles['access-column']}>
                <InformationField heading="Brands" information={renderBrands(userBrands || [])} />
              </div>
            </div>
          </DetailsPanel>
        </div>
      </div>
    </>
  );
};
